/**
 * Navbar - Main navigation component
 * Responsive navigation with mobile hamburger menu, search, and user actions
 * Integrates with authentication state and provides consistent navigation
 */

import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Button from '../ui/Button';
import { logger } from '../../utils/logger';
// import SearchBar from '../search/SearchBar';

export default function Navbar() {
  const { state: { isAuthenticated, user }, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);

  // Helper function to determine if a link is active
  const isActiveLink = (path: string): boolean => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  // Helper function to get link classes with active state
  const getLinkClasses = (path: string): string => {
    const baseClasses = 'transition-colors font-medium';
    const activeClasses = 'text-primary-600 border-b-2 border-primary-600';
    const inactiveClasses = 'text-gray-700 hover:text-primary-600 border-b-2 border-transparent hover:border-primary-200';
    
    return `${baseClasses} ${isActiveLink(path) ? activeClasses : inactiveClasses}`;
  };

  const handleCreatePrompt = () => {
    if (!isAuthenticated) {
      navigate(`/login?returnUrl=${encodeURIComponent('/prompts/create')}`);
      return;
    }
    navigate('/prompts/create');
  };

  const handleLogout = async () => {
    try {
      await logout();
      setIsProfileMenuOpen(false);
      navigate('/');
    } catch (error) {
      logger.child('AUTH').error('Logout failed:', error);
    }
  };

  return (
    <>
      <nav className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-40" role="banner">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <div className="flex items-center">
              <Link to="/" className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center">
                  <svg
                    className="w-5 h-5 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 10V3L4 14h7v7l9-11h-7z"
                    />
                  </svg>
                </div>
                <span className="text-xl font-bold text-gray-900">
                  PromptShare
                </span>
              </Link>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8 h-16">
              <Link
                id="nav-home"
                to="/"
                className={`${getLinkClasses('/')} px-1 py-4 h-full flex items-center`}
              >
                Home
              </Link>
              <Link
                id="nav-browse"
                to="/prompts"
                className={`${getLinkClasses('/prompts')} px-1 py-4 h-full flex items-center`}
              >
                Browse
              </Link>

              <Link
                id="nav-chat"
                to="/chat"
                className={`${getLinkClasses('/chat')} px-1 py-4 h-full flex items-center`}
              >
                Chat
              </Link>
              <Link
                id="nav-my-prompts"
                to="/my-prompts"
                className={`${getLinkClasses('/my-prompts')} px-1 py-4 h-full flex items-center`}
              >
                My Prompts
              </Link>
              <Link
                id="nav-saved"
                to="/saved"
                className={`${getLinkClasses('/saved')} px-1 py-4 h-full flex items-center`}
              >
                Saved
              </Link>

            </div>

            {/* Search Bar (Desktop) */}
            <div className="hidden md:flex flex-1 max-w-lg mx-8">
              {/* <SearchBar
                size="md"
                className="w-full"
              /> */}
            </div>

            {/* Desktop Actions */}
            <div className="hidden md:flex items-center space-x-4">
              <Button
                id="nav-create-prompt"
                onClick={handleCreatePrompt}
                variant="primary"
                size="sm"
              >
                Create Prompt
              </Button>

              {isAuthenticated ? (
                <div className="relative">
                  <button
                    onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
                    className="flex items-center space-x-2 p-2 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <div className="w-8 h-8 bg-primary-600 rounded-full flex items-center justify-center">
                      <span className="text-white text-sm font-medium">
                        {user?.name?.charAt(0).toUpperCase() || 'U'}
                      </span>
                    </div>
                    <svg
                      className="w-4 h-4 text-gray-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>

                  {/* Profile Dropdown */}
                  {isProfileMenuOpen && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                      <div className="px-4 py-2 border-b border-gray-200">
                        <p className="text-sm font-medium text-gray-900">
                          {user?.name}
                        </p>
                        <p className="text-sm text-gray-500">
                          {user?.email}
                        </p>
                      </div>
                      {/* <Link
                        to="/profile"
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setIsProfileMenuOpen(false)}
                      >
                        Profile
                      </Link> */}
                      <Link
                        to="/my-prompts"
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setIsProfileMenuOpen(false)}
                      >
                        My Prompts
                      </Link>
                      <Link
                        to="/saved"
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setIsProfileMenuOpen(false)}
                      >
                        Saved
                      </Link>
                      {user?.role === 'admin' && (
                        <Link
                          to="/admin"
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          onClick={() => setIsProfileMenuOpen(false)}
                        >
                          Admin
                        </Link>
                      )}
                      <hr className="my-1" />
                      <button
                        onClick={handleLogout}
                        className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                      >
                        Sign Out
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <Button
                  onClick={() => navigate('/login')}
                  variant="outline"
                  size="sm"
                >
                  Sign In
                </Button>
              )}
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden">
              <button
                id="mobile-menu-toggle-btn"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="p-2 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-100"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  {isMobileMenuOpen ? (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  ) : (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6h16M4 12h16M4 18h16"
                    />
                  )}
                </svg>
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {isMobileMenuOpen && (
            <div className="md:hidden border-t border-gray-200 py-4">
              {/* Mobile Navigation */}
              <div className="space-y-1 px-4">
                <Link
                  id="mobile-nav-home"
                  to="/"
                  className={`block py-2 px-3 rounded-lg ${
                    isActiveLink('/') 
                      ? 'text-primary-600 bg-primary-50 font-medium' 
                      : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50'
                  }`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Home
                </Link>
                <Link
                  id="mobile-nav-browse"
                  to="/prompts"
                  className={`block py-2 px-3 rounded-lg ${
                    isActiveLink('/prompts') 
                      ? 'text-primary-600 bg-primary-50 font-medium' 
                      : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50'
                  }`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Browse
                </Link>

                <Link
                  id="mobile-nav-chat"
                  to="/chat"
                  className={`block py-2 px-3 rounded-lg ${
                    isActiveLink('/chat')
                      ? 'text-primary-600 bg-primary-50 font-medium'
                      : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50'
                  }`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Chat
                </Link>

                
                <div className="pt-4 border-t border-gray-200">
                  <Button
                    id="mobile-nav-create-prompt"
                    onClick={() => {
                      handleCreatePrompt();
                      setIsMobileMenuOpen(false);
                    }}
                    variant="primary"
                    size="sm"
                    className="w-full mb-2"
                  >
                    Create Prompt
                  </Button>

                  {/* Always show My Prompts and Saved in the lower mobile section */}
                  <div className="space-y-1 mb-2">
                    <Link
                      id="mobile-nav-my-prompts"
                      to="/my-prompts"
                      className="block py-2 text-sm text-gray-700 hover:text-primary-600"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      My Prompts
                    </Link>
                    <Link
                      id="mobile-nav-saved"
                      to="/saved"
                      className="block py-2 text-sm text-gray-700 hover:text-primary-600"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      Saved
                    </Link>
                  </div>

                  {isAuthenticated ? (
                    <div className="space-y-1">
                      {user?.role === 'admin' && (
                        <Link
                          to="/admin"
                          className="block py-2 text-sm text-gray-700 hover:text-primary-600"
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          Admin
                        </Link>
                      )}
                      <button
                        onClick={() => {
                          handleLogout();
                          setIsMobileMenuOpen(false);
                        }}
                        className="block w-full text-left py-2 text-sm text-red-600 hover:text-red-700"
                      >
                        Sign Out
                      </button>
                    </div>
                  ) : (
                    <Button
                      onClick={() => {
                        navigate('/login');
                        setIsMobileMenuOpen(false);
                      }}
                      variant="outline"
                      size="sm"
                      className="w-full"
                    >
                      Sign In
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </nav>
    </>
    )
}